import * as React from 'react';
import {
  Controller,
  type Control,
  type FieldValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import styled from 'styled-components';

import { parseDateLocal } from '@packages/core';
import { Radio, Typography } from '@packages/ui/primitives';
import { DatePicker } from '@packages/ui/composites/DatePicker';
import { NumberInput } from '@packages/ui/primitives/NumberInput';
import { FormField } from '@packages/ui/patterns/FormField';
import { rem } from '@packages/ui/tokens/spacing';
import { rhfInlineError } from '@packages/ui/hook-form-adapters/rhfInlineError';

/** Legacy `RadioButtonWrapper` + `fix-alignment` in `customRecurrenceModal.tsx`. */
const EndsRadio = styled(Radio)`
  gap: ${rem(17)};

  .radio-label {
    margin-top: ${rem(2)};
  }
`;

const EndsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`;

const EndsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: ${rem(14)};
  column-gap: ${rem(16)};
`;

const GridCell = styled.div`
  display: flex;
  align-items: center;
`;

/** Second column is `1fr`; legacy wraps the picker in `w-64` so it does not stretch. */
const DateGridCell = styled(GridCell)`
  justify-self: start;
  width: auto;
  max-width: 100%;
`;

const DateFieldShell = styled.div`
  display: inline-block;
  width: ${rem(156)};
  max-width: ${rem(156)};
  box-sizing: border-box;
  vertical-align: middle;

  & > div {
    width: ${rem(156)};
    max-width: ${rem(156)};
  }

  .wl-datepicker-ds,
  .react-datepicker-wrapper {
    width: ${rem(156)};
    max-width: ${rem(156)};
  }
`;

const OccurrencesInputShell = styled.div`
  width: ${rem(80)};
  min-width: 0;
`;

const AfterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`;

export interface RhfCustomRecurrenceEndsProps {
  control: Control<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
}

/**
 * Job-scheduler custom recurrence "Ends" section (legacy `customRecurrenceModal.tsx` parity).
 */
export function RhfCustomRecurrenceEnds({ control }: RhfCustomRecurrenceEndsProps): React.ReactElement {
  const endType = useWatch({ control, name: 'endType' });
  const anchorRaw = useWatch({ control, name: 'recurrenceStartDateAnchor' });
  const minDate = React.useMemo(() => {
    if (anchorRaw == null || anchorRaw === '') return undefined;
    return parseDateLocal(String(anchorRaw));
  }, [anchorRaw]);

  return (
    <EndsSection data-semantic-id='endsSection' className='mt-6'>
      <Typography variant='body-field' semanticId='endsLabel'>
        Ends
      </Typography>
      <Controller
        control={control}
        name='endType'
        render={({ field, fieldState, formState }) => {
          const endTypeError = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const selected = String(field.value ?? '');

          const radio = (value: string, label: string, id: string) => (
            <EndsRadio
              id={id}
              name='endType'
              value={value}
              checked={selected === value}
              error={Boolean(endTypeError)}
              semanticId={id}
              onChange={() => field.onChange(value)}
              onBlur={field.onBlur}
            >
              {label}
            </EndsRadio>
          );

          return (
            <>
              <EndsGrid id='endOptionsContainer'>
                <GridCell id='endNeverOption'>{radio('never', 'Never', 'endTypeNever')}</GridCell>
                <GridCell id='endNeverSpacer' aria-hidden='true' />
                <GridCell id='endOnOption'>{radio('on', 'On', 'endTypeOn')}</GridCell>
                <DateGridCell
                  id='endOnDateContainer'
                  onClick={(e) => e.stopPropagation()}
                >
                  <DateFieldShell>
                    <Controller
                      control={control}
                      name='endOn'
                      render={({ field: dateField, fieldState: dateState, formState: fs }) => {
                        const dateError = rhfInlineError(dateState, { isSubmitted: fs.isSubmitted });
                        const disabled = endType !== 'on';
                        const dateValue =
                          dateField.value instanceof Date
                            ? dateField.value
                            : dateField.value != null && dateField.value !== ''
                              ? parseDateLocal(dateField.value as string | number)
                              : null;
                        const dateFieldWidth = rem(256);

                        return (
                          <FormField
                            label=''
                            required={false}
                            semanticId='endOn'
                            disabled={disabled}
                            error={dateError}
                            reserveLabelSpace={false}
                            controlWidth='full'
                            style={{ width: dateFieldWidth, maxWidth: dateFieldWidth }}
                          >
                            <DatePicker
                              name='endOn'
                              value={dateValue}
                              style={{ width: dateFieldWidth, maxWidth: dateFieldWidth }}
                              onChange={(date) => {
                                const formatted =
                                  date instanceof Date
                                    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                                    : (date ?? '');
                                dateField.onChange(formatted);
                              }}
                              onInputBlur={dateField.onBlur}
                              invalid={Boolean(dateError)}
                              disabled={disabled}
                              semanticId='endOn'
                              minDate={minDate}
                            />
                          </FormField>
                        );
                      }}
                    />
                  </DateFieldShell>
                </DateGridCell>
                <GridCell id='endAfterOption'>{radio('after', 'After', 'endTypeAfter')}</GridCell>
                <GridCell
                  id='endAfterInputContainer'
                  onClick={(e) => e.stopPropagation()}
                >
                  <AfterRow>
                    <OccurrencesInputShell id='occurrencesLimitInputContainer'>
                      <Controller
                        control={control}
                        name='occurrencesLimit'
                        render={({ field: numField, fieldState: numState, formState: fs }) => {
                          const numError = rhfInlineError(numState, { isSubmitted: fs.isSubmitted });
                          const disabled = endType !== 'after';

                          return (
                            <FormField
                              label=''
                              required={false}
                              semanticId='occurrencesLimit'
                              disabled={disabled}
                              error={numError}
                              controlWidth='full'
                              reserveLabelSpace={false}
                            >
                              <NumberInput
                                name='occurrencesLimit'
                                value={numField.value ?? ''}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  numField.onChange(raw === '' ? '' : Number(raw));
                                }}
                                onBlur={numField.onBlur}
                                disabled={disabled}
                                error={Boolean(numError)}
                                semanticId='occurrencesLimit'
                                placeholder='10'
                              />
                            </FormField>
                          );
                        }}
                      />
                    </OccurrencesInputShell>
                    <Typography variant='body-s' semanticId='occurrencesLabel'>
                      occurrences
                    </Typography>
                  </AfterRow>
                </GridCell>
              </EndsGrid>
              {endTypeError ? (
                <Typography variant='body-xs' color='var(--ui-danger)' role='alert' style={{ marginTop: rem(8) }}>
                  {endTypeError}
                </Typography>
              ) : null}
            </>
          );
        }}
      />
    </EndsSection>
  );
}
